import {RefObject, useLayoutEffect, useMemo, useRef, useState} from 'react';

import {CrmFieldModel, CrmSyncModel} from '../../../../../models/api/crmSyncModel';
import {CustomFieldModel} from '../../../../../models/api/customFieldModel';
import {computeCrmSyncMapping, CrmToFrontCustomFieldMapping} from '../crmSyncHelpers';

export function useMappedFields(
  contentWrapperRef: RefObject<HTMLDivElement>,
  customFields: readonly CustomFieldModel[],
  crmSync?: CrmSyncModel,
  crmFields?: readonly CrmFieldModel[],
) {
  const [mappedFields, setMappedFields] = useState(computeCrmSyncMapping(crmSync));
  const addedNewMappedField = useRef<boolean>(false);

  const onRemoveMappedField = (crmFieldId: string) =>
    setMappedFields((fields) => fields.filter((field) => field.crmFieldId !== crmFieldId));

  const onAddMappedField = (mapping: CrmToFrontCustomFieldMapping) => {
    addedNewMappedField.current = true;
    setMappedFields((fields) => [...fields, mapping]);
  };

  const onUpdateMappedField = (crmFieldId: string, customFieldId?: number, newCrmFieldId?: string) => {
    setMappedFields((fields) =>
      fields.map((field) => {
        if (field.crmFieldId === crmFieldId) {
          return {crmFieldId: newCrmFieldId ?? crmFieldId, customFieldId};
        }
        return field;
      }),
    );
  };

  const onResetMappedFields = (mappings: readonly CrmToFrontCustomFieldMapping[]) => {
    setMappedFields(mappings);
  };

  useLayoutEffect(() => {
    // Make sure we have a ref and we have a new mapped field added.
    if (!contentWrapperRef.current || !addedNewMappedField.current) {
      return;
    }

    // Mark as added to false and scroll to the bottom.
    addedNewMappedField.current = false;
    contentWrapperRef.current.scrollTo({
      top: contentWrapperRef.current.scrollHeight,
    });
  });

  const hasInvalidMappedField = useMemo(() => {
    if (!crmFields) {
      return true;
    }

    // If a front custom field was deleted or they do not have a custom field selected.
    if (
      mappedFields.some((field) => {
        if (!field.customFieldId) {
          return true;
        }
        // If the field is less than 0, we are dealing with some default fields.
        if (field.customFieldId < 0) {
          return false;
        }
        return !customFields.find((customField) => customField.id === field.customFieldId);
      })
    ) {
      return true;
    }

    // If a custom field was deleted.
    if (mappedFields.some((field) => !crmFields.find((crmField) => crmField.id === field.crmFieldId))) {
      return true;
    }

    return false;
  }, [customFields, mappedFields, crmFields]);

  return {
    mappedFields,
    hasInvalidMappedField,
    onResetMappedFields,
    onRemoveMappedField,
    onAddMappedField,
    onUpdateMappedField,
  };
}
